/**
 * Created by neo on 10.01.2026
 * Challenge Header Component - Hero section with image, title, and status
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { IndividualChallenge } from '../../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { Media, getMediaLarge } from '../../../Model/Media/Media';
import { Theme } from '../../../Theme/Theme';
import { ChallengeType } from '../ChallengeDetailScreen';

const HeaderContainer = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%);
  color: white;
`;

const HeroImage = styled.div<{ imageUrl?: string }>`
  position: relative;
  width: 100%;
  height: 300px;
  background: ${(props) => (props.imageUrl ? `url(${props.imageUrl})` : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`)};
  background-size: cover;
  background-position: center;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
  }

  @media (min-width: 768px) {
    height: 350px;
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const StatusBadge = styled.div<{ status: string }>`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.status) {
      case 'running':
        return Theme.Colors.success;
      case 'pending':
        return Theme.Colors.orange;
      case 'ended':
        return Theme.Colors.charcoal;
      default:
        return Theme.Colors.charcoal;
    }
  }};
  color: white;
`;

const ContentOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  z-index: 5;
`;

const ChallengeTypeBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.2);
  margin-bottom: 12px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  @media (min-width: 768px) {
    font-size: 36px;
  }
`;

const Description = styled.p`
  font-size: 14px;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const DateRange = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
`;

const DateIcon = styled.span`
  font-size: 16px;
`;

export type ChallengeHeaderProps = {
  challenge: RouteChallenge | IndividualChallenge;
  challengeType: ChallengeType;
  challengeName: string;
  challengeDescription: string;
  challengeImage?: Media;
  onBack: () => void;
};

export const ChallengeHeader: React.FC<ChallengeHeaderProps> = observer(
  ({ challenge, challengeType, challengeName, challengeDescription, challengeImage, onBack }) => {
    const { t } = useTrans();

    const status = 'challengeType' in challenge && challenge.challengeType === 'individual' ? challenge.status : (challenge.ended ? 'ended' : challenge.started ? 'running' : 'pending');

    const getStatusLabel = () => {
      switch (status) {
        case 'running':
          return t('challenge.status.active', { defaultValue: 'Active' });
        case 'pending':
          return t('challenge.status.upcoming', { defaultValue: 'Upcoming' });
        case 'ended':
          return t('challenge.status.ended', { defaultValue: 'Ended' });
        default:
          return '';
      }
    };

    const startDate = dayjs(challenge.startDateTime).format('MMM D, YYYY');
    const endDate = dayjs(challenge.endDateTime).format('MMM D, YYYY');

    return (
      <HeaderContainer>
        <HeroImage imageUrl={challengeImage ? getMediaLarge(challengeImage) : undefined}>
          <BackButton onClick={onBack}>←</BackButton>
          <StatusBadge status={status}>{getStatusLabel()}</StatusBadge>
          <ContentOverlay>
            <ChallengeTypeBadge>
              {challengeType === 'individual'
                ? t('challenge.type.individual', { defaultValue: 'Individual Challenge' })
                : t('challenge.type.route', { defaultValue: 'Team Challenge' })}
            </ChallengeTypeBadge>
            <Title>{challengeName}</Title>
            {challengeDescription && <Description>{challengeDescription}</Description>}
            <DateRange>
              <DateIcon>📅</DateIcon>
              <span>
                {startDate} - {endDate}
              </span>
            </DateRange>
          </ContentOverlay>
        </HeroImage>
      </HeaderContainer>
    );
  },
);
